import { type Diagnostic, linter } from '@codemirror/lint';
import type { Extension } from '@codemirror/state';
import type { EditorView } from '@codemirror/view';

import type { HelperError } from './utils/error-collector';

function toDiagnostics(
  content: string,
  issues: HelperError[],
  view: EditorView
): Diagnostic[] {
  const mapSeverity = (
    severity: HelperError['severity']
  ): Diagnostic['severity'] => {
    if (severity === 'error') return 'error';
    if (severity === 'warning') return 'warning';
    return 'info';
  };

  return issues.map((issue) => {
    let from = 0;
    let to = Math.min(1, content.length);

    if (issue.position) {
      try {
        const doc = view.state.doc;
        const startLine = doc.line(
          Math.max(1, Math.min(issue.position.line, doc.lines))
        );
        from = Math.min(startLine.from + issue.position.column, startLine.to);

        if (
          issue.position.endLine !== undefined &&
          issue.position.endColumn !== undefined
        ) {
          const endLine = doc.line(
            Math.max(1, Math.min(issue.position.endLine, doc.lines))
          );
          to = Math.min(endLine.from + issue.position.endColumn, endLine.to);
        } else {
          to = Math.min(from + (issue.helperName?.length || 1), startLine.to);
        }
      } catch {
        from = 0;
        to = Math.min(1, content.length);
      }
    }

    return {
      from,
      to: Math.max(from, to),
      severity: mapSeverity(issue.severity),
      message: issue.message,
    };
  });
}

export function linterExtension(
  validate: (content: string) => Promise<HelperError[]>
): Extension {
  return linter(
    async (view): Promise<Diagnostic[]> => {
      const content = view.state.doc.toString();
      const issues = await validate(content);
      return toDiagnostics(content, issues, view);
    },
    { delay: 300 }
  );
}
